import { TableType } from 'models/WidgetModel';
import { extractValue } from 'helpers/sorting';

// eslint-disable-next-line prettier/prettier
export type SortingTypeKey = keyof typeof TableType;
export type SortingTypeValue = (typeof TableType)[SortingTypeKey];

interface SortingResult {
  tableType: SortingTypeValue;
  isSortable: boolean;
  requiresEnterprisePreparation: boolean;
}

export function resolveSortingType(payload: any): SortingResult {
  const isSortable = payload?.isSortable ?? payload?.IsSortable;
  const isEnterprise = payload?.isEnterprise ?? payload?.IsEnterprise;
  const hasQueriesMetadata = payload?.QueriesMetadata?.length > 0;

  const tableType = isEnterprise ? TableType.ENTERPRISE : TableType.NORMAL;
  let requiresEnterprisePreparation = false;

  /*
  if (isSortable) {
    tableType = isEnterprise ? TableType.ENTERPRISE : TableType.NORMAL;
  } else if (hasQueriesMetadata) {
    tableType = TableType.NONE;
  } else {
    tableType = TableType.NORMAL;
  }
  */

  if (tableType === TableType.ENTERPRISE && isSortable) {
    requiresEnterprisePreparation = true;
  }

  return { tableType, isSortable, requiresEnterprisePreparation };
}

//  Compares two values and returns the order based on the provided direction
const sortValues = (a: any, b: any, direction: 'asc' | 'desc'): number => {
  const normalize = (value: any) => {
    if (typeof value === 'number') return value;
    if (typeof value === 'string') return value;
    return '';
  };

  const aNorm = normalize(a);
  const bNorm = normalize(b);

  // Both values are numbers
  if (typeof aNorm === 'number' && typeof bNorm === 'number') {
    return direction === 'asc' ? aNorm - bNorm : bNorm - aNorm;
  }

  // Both values are strings or other types
  const aStr = String(aNorm);
  const bStr = String(bNorm);
  const result = aStr.localeCompare(bStr, undefined, { numeric: true, sensitivity: 'base' });

  return direction === 'asc' ? result : -result;
};

export const getCustomSorting =
  (mode: SortingTypeValue, showTotals: boolean, totalsFirstRow: any, direction: 'asc' | 'desc') =>
  (rowA: any, rowB: any, columnId: string): number => {
    const a = rowA.original;
    const b = rowB.original;

    // If the table has totals and the current row is a totals row, we want to keep it at the top
    if (showTotals) {
      const isTotalsA = a?.id === totalsFirstRow?.current?.id;
      const isTotalsB = b?.id === totalsFirstRow?.current?.id;

      if (isTotalsA || isTotalsB) return 0;
    }

    // If the table is in enterprise mode, we want to keep the L1 rows at the respective workspace top
    if (mode === TableType.ENTERPRISE) {
      if (a.rowType === 'L1' || b.rowType === 'L1') return 0;
      if (a.workspaceId !== b.workspaceId) return 0;
    }

    const aRaw = a[columnId];
    const bRaw = b[columnId];

    const aValue = aRaw?.value !== undefined ? extractValue(aRaw.value) : extractValue(aRaw);
    const bValue = bRaw?.value !== undefined ? extractValue(bRaw.value) : extractValue(bRaw);

    return sortValues(aValue, bValue, direction);
  };

export const enableSorting = (column: any, defaultResponse: any, payload: any): boolean => {
  if (payload?.isEnterprise) {
    return payload?.isSortable;
  }
  if (!column?.isLink || !defaultResponse?.data?.idSurveyPanel) {
    return true;
  }
  return column.displayName !== '';
};
